<template>
    <v-container fluid class="pb-0">
        <v-row class="pb-2">
            <v-col cols="2">
                <v-btn
                    block
                    :disabled="disabledWrite"
                    @click="dialogSelectAccountingFirm = !dialogSelectAccountingFirm"
                    variant="flat"
                >
                    Ajouter
                </v-btn>
            </v-col>
            <v-col class="pl-2">
                <v-text-field
                    v-model="search"
                    label="Recherche"
                    hide-details
                    density="compact"
                    append-inner-icon="$search"
                />
            </v-col>
            <v-col cols="1" class="text-center">
                <v-btn-trash @click="initialize" class="mx-2" size="small" />
            </v-col>
        </v-row>

        <v-row
            v-if="accountingFirms.length"
            class="d-flex justify-center"
            style="max-height: 84vh; overflow-y: auto"
        >
            <v-col
                :cols="isMobile ? 12 : 8"
                class="my-0 pa-2"
                v-for="accFirm in childAccountingFirms"
                :key="accFirm.id"
            >
                <v-card class="default_card ma-0 pa-0" elevation="0" style="margin: 5px">
                    <v-row class="d-flex align-center" @click="openAccountingFirm(accFirm.id)">
                        <v-col cols="2" v-if="!isMobile">
                            <v-icon color="secondary" size="large" class="icon">
                                $apartment
                            </v-icon>
                        </v-col>
                        <v-col :cols="isMobile ? 10 : 8">
                            <div class="d-flex align-center primary--text">
                                <span
                                    :class="isMobile ? 'ml-2' : ''"
                                    style="font-weight: bold; font-size: 16pt"
                                >
                                    {{ accFirm.name }}
                                </span>
                            </div>
                            <div class="d-flex align-center">
                                <span :class="isMobile ? 'ml-2' : ''">
                                    SIRET: {{ getSiretValue(accFirm.siret) }}
                                </span>
                            </div>
                        </v-col>
                        <v-col cols="2" class="d-flex justify-space-around align-center">
                            <v-btn icon="$oss-edit" color="secondary" elevation="0" size="small" />
                            <v-btn
                                icon="$oss-disconnect"
                                color="#f76631"
                                elevation="0"
                                :disabled="disabledWrite"
                                @click.stop="
                                    selectedAccountingFirm = accFirm;
                                    dialogDisconnectAccountingFirm = true;
                                "
                                size="small"
                            />
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-else class="d-flex justify-center">
            <v-col :cols="isMobile ? 12 : 6" class="my-0 pa-2">
                <v-card class="default_card ma-0 pa-0" elevation="0" style="margin: 5px">
                    <v-row class="d-flex align-center">
                        <v-col cols="2" v-if="!isMobile">
                            <v-icon color="error" size="large" class="icon"> $apartment </v-icon>
                        </v-col>
                        <v-col :cols="isMobile ? 10 : 8">
                            <div class="d-flex align-center primary--text">
                                <span
                                    :class="isMobile ? 'ml-2' : ''"
                                    style="font-weight: bold; font-size: 16pt"
                                >
                                    Aucun sous cabinet
                                </span>
                            </div>
                            <div class="d-flex align-center">
                                <span :class="isMobile ? 'ml-2' : ''">
                                    Vous n'avez enregistré aucun sous cabinet
                                </span>
                            </div>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>
        </v-row>
        <!-- ThirdPartyaccountingFirm -->
        <template>
            <OssDialog
                v-model="dialogSelectAccountingFirm"
                :title="'Ajouter un cabinet'"
                width="1000px"
                v-if="dialogSelectAccountingFirm"
            >
                <CompaniesTable
                    :client-company="false"
                    :accounting-firm-company="true"
                    :show-generic-company="false"
                    :show-new-companies="true"
                    :show-existing-companies="false"
                    :accountingFirmId="accountingFirm.id"
                    @select-company="accountingFirmSelected"
                    :wizard-mode="true"
                    :is-enable-accounting-firms="true"
                    :can-select-companies-with-accounting-company="true"
                />
            </OssDialog>

            <OssDialog
                v-model="dialogDisconnectAccountingFirm"
                :max-width="isMobile ? undefined : '650px'"
                :fullscreen="isMobile"
                :hide-overlay="isMobile"
                title="Enlever la société cliente"
                valid-title="Retirer"
                @valid="deleteAccountingFirm(selectedAccountingFirm)"
            >
                <v-row class="mx-2">
                    <v-col>
                        Vous êtes sur le point de retirer le cabinet
                        <b>{{ selectedAccountingFirm.name }}</b> de vos sous cabinet. Confirmer le
                        retrait ?
                    </v-col>
                </v-row>
            </OssDialog>

            <OssDialog
                v-model="newAccountingFirmDialog"
                :max-width="isMobile ? undefined : '80%'"
                :fullscreen="isMobile"
                :hide-overlay="isMobile"
                :actions="actions"
                title="Création / Edition du cabinet"
            >
                <EditCompany
                    :settings-mode="true"
                    :admin-mode="true"
                    :in-company="newAccountingFirm"
                    :actions="actions"
                    @updated="accountingFirmSelected($event)"
                />
            </OssDialog>
        </template>
    </v-container>
</template>

<script lang="ts">
import gql from "graphql-tag";
import _ from "lodash";
import { Apollo } from "@/decorators";
import { useDisplay } from "vuetify";
import { Company } from "../../../../types";

import UPDATE_ACCOUNTING_FIRMS from "../../graphql/company/UpdateAccountingFirms.gql";

import CompaniesTable from "../admin/CompaniesTable.vue";

import { DialogAction } from "../../services/dialogAction";
import EditCompany from "../EditCompany.vue";

import { getSiretValue } from "../../../src/services/companyServices";
import { updateStoreUser } from "../../../src/services/userServices";
import { User } from "../../store/store";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import emitter from "@/services/emitter";
import BobbeeImg from "../../assets/img/bobbee3d/bobbee3dTuto.png";
import { computed } from "vue";

@Component({
    components: {
        CompaniesTable: CompaniesTable,
        EditCompany,
    },
    emits: ["refresh"],
})
export class AccountingFirms extends Vue {
    @Prop({ default: [] }) accountingFirm: any;
    newAccountingFirmName: string = "";
    isMobile;
    dialogSelectAccountingFirm: boolean = false;
    verifLoading: boolean = false;
    loadingValidate: boolean = false;
    newAccountingFirm: any = {
        text: "",
    };
    actions: any = {
        action: new DialogAction(),
    };
    dialogDisconnectAccountingFirm: boolean = false;
    newAccountingFirmDialog: boolean = false;

    duplicateAccountingFirmDialog: boolean = false;
    duplicateAccountingFirmLoading: boolean = false;

    accountingFirmWizardStep = 1;

    selectedAccountingFirm: any = {
        id: "",
        siret: null,
        text: null,
    };

    newAccountingFirmText = "";

    companiesHeaders: Array<any> = [
        { title: "", value: "" },
        { title: "Nom", value: "date" },
        { title: "Siret", value: "amount" },
    ];

    search = "";

    get currentUser(): User {
        return this.$store.state.user;
    }

    get isEnableAccountingFirms() {
        return this.accountingFirm.enableAccountingFirms;
    }

    get disabledWrite() {
        if (!this.currentUser) {
            return true;
        }
        if (this.currentUser.profile.type !== "ROOT") {
            return true;
        }
        if (!this.currentUser.company?.id) {
            return true;
        }
        if (
            !this.currentUser.company.accountingFirm ||
            !this.currentUser.company.enableAccountingFirms
        ) {
            return true;
        }
        if (this.currentUser.isSubUserOf?.id) {
            return true;
        }
        return false;
    }

    get childAccountingFirms() {
        const array = [];
        const accountingFirms = !this.isEnableAccountingFirms ? this.accountingFirms : [];
        let firms = accountingFirms.sort((a: any, b: any) => a.name.localeCompare(b.name));
        if (this.search) {
            firms = firms.filter((x: { name: string }) =>
                x.name.toLowerCase().includes(this.search.toLowerCase()),
            );
        }
        return firms.sort((a: any, b: any) => a.name.localeCompare(b.name));
    }

    initialize() {
        this.selectedAccountingFirm = {
            id: "",
            siret: null,
            name: null,
        };
        this.newAccountingFirmName = "";
        this.search = "";
        this.selectedAccountingFirm = "";
    }

    beforeMount() {
        this.initialize();
    }

    @Apollo({
        query: gql`
            query companyDetails($id: ID!) {
                company(where: { id: $id }) {
                    id
                    accountingFirms {
                        id
                        name
                        siret
                        profile {
                            id
                            accountingType
                        }
                        legalForm
                    }
                }
            }
        `,
        variables() {
            return {
                id: this.accountingFirm.id,
            };
        },
        skip() {
            return !this.accountingFirm?.id;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data && data.company) {
                    if (data.company.accountingFirms) {
                        this.accountingFirms = data.company.accountingFirms;
                    } else {
                        this.accountingFirms = [];
                    }
                }
            }
        },
    })
    accountingFirms: any = [];

    async accountingFirmSelected(accountingFirm: Company) {
        if (accountingFirm.id && this.accountingFirm.id === accountingFirm.id) {
            debugger;
            return;
        }

        if (accountingFirm.id) {
            debugger;

            const existingAccountingFirm = this.accountingFirms.find(
                (cc: { id: string }) => cc.id === accountingFirm.id,
            );
            if (!existingAccountingFirm) {
                this.accountingFirms.push(accountingFirm);
                this.dialogSelectAccountingFirm = false;
                this.newAccountingFirmDialog = false;
                await this.addAccountingFirm(accountingFirm);
            } else {
                debugger;
            }
        } else {
            this.newAccountingFirm = { ...accountingFirm };
            this.newAccountingFirm.accountingFirm = true;
            this.newAccountingFirmDialog = true;
            this.dialogSelectAccountingFirm = false;
        }
    }

    async addAccountingFirm(accountingFirm: Company) {
        if (this.accountingFirm.id) {
            debugger;

            const updateAccountingFirms = await this.$apollo.mutate({
                mutation: UPDATE_ACCOUNTING_FIRMS,
                variables: {
                    id: this.accountingFirm.id,
                    accountingFirms: this.accountingFirms.map((aF) => aF.id),
                },
            });
            if (updateAccountingFirms.data.updateAccountingFirms) {
                this.$emit("refresh");
            }
        }
    }

    async deleteAccountingFirm(accountingFirm: Company) {
        if (accountingFirm.id) {
            const index = this.accountingFirms.findIndex(
                (cc: { id: string }) => cc.id === accountingFirm.id,
            );
            if (index !== -1) {
                this.accountingFirms.splice(index, 1);
            }
            const updateAccountingFirms = await this.$apollo.mutate({
                mutation: UPDATE_ACCOUNTING_FIRMS,
                variables: {
                    id: this.accountingFirm.id,
                    accountingFirms: this.accountingFirms.map((aF) => aF.id),
                    accountingFirmIdToRemove: accountingFirm.id,
                },
            });
            if (updateAccountingFirms.data.updateAccountingFirms) {
                this.$emit("refresh");
                this.dialogDisconnectAccountingFirm = false;
            }
        }
    }

    help() {
        document.location.href = "http://bit.ly/2Zo5ZY6";
    }

    startTuto() {
        emitter.$emit("setTutorialData", [
            {
                ref: null,
                title: "Tuto vidéo",
                pages: [
                    {
                        text: "La validation des factures se fait en plusieurs étapes.",
                        img: BobbeeImg,
                        video: "https://bobbee-videos.s3.eu-central-1.amazonaws.com/gi-creation-client.mp4",
                        outed: true,
                    },
                ],
            },
        ]);
    }

    created() {
        const { xsOnly } = useDisplay();
        this.isMobile = computed(() => xsOnly.value);
    }

    getSiretValue(siret: string) {
        return getSiretValue(siret);
    }

    async openAccountingFirm(accountingFirmId) {
        if (this.currentUser.company.id === accountingFirmId) {
            // nothing to do
        } else if (this.currentUser.isSubUserOf?.company?.id === accountingFirmId) {
            this.$store.commit("updateSubUserId", null);
            // this.$store.commit('updateAccountingFirmId', accountingFirmId)
            await updateStoreUser(this.$apollo, this.$store, this.currentUser.isSubUserOf.id);
        } else if (this.currentUser.subUsers?.length) {
            for (const subUser of this.currentUser.subUsers) {
                if (subUser.company?.id === accountingFirmId) {
                    this.$store.commit("updateSubUserId", subUser.id);
                    // this.$store.commit('updateAccountingFirmId', accountingFirmId)
                    await updateStoreUser(this.$apollo, this.$store, subUser.id);
                    break;
                }
            }
        }
    }
}

export default toNative(AccountingFirms);
</script>

<style scoped lang="scss">
.error,
.warning {
    color: #fff;
}
</style>
